import { Component, type ErrorInfo, type ReactNode } from "react";
import { Container, Title, Text, Button, Stack, Paper } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { captureFrontendException } from "../observability/sentry";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureFrontendException(error, {
      componentStack: errorInfo.componentStack ?? "Unavailable",
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Container size="sm" py="xl">
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
              <Title order={3}>Something went wrong</Title>
              <Text c="dimmed" ta="center">
                An unexpected error occurred. Please try again or contact support if the problem persists.
              </Text>
              <Button onClick={() => this.setState({ hasError: false, error: null })}>
                Try Again
              </Button>
            </Stack>
          </Paper>
        </Container>
      );
    }
    return this.props.children;
  }
}
